import { translate } from "@/plugins/i18n";
import type BaseEnumType from "../../baseEnumType";
import Key from "@/app/types/common/key";
import { Maybe } from "@/components/utils/typeHelpers";


export class EnumActivityLogType implements BaseEnumType {
  private static allValues: EnumActivityLogType[] = []; // make sure this is top-most
  
  public static none = new EnumActivityLogType(0, "None", "enum_activity_log_type_none");
  public static orderCreated = new EnumActivityLogType(1, "OrderCreated", "enum_activity_log_type_order_created");
  public static orderCancelled = new EnumActivityLogType(2, "OrderCancelled", "enum_activity_log_type_order_cancelled");
  public static orderCompleted = new EnumActivityLogType(3, "OrderCompleted", "enum_activity_log_type_order_completed");
  public static creditCreated = new EnumActivityLogType(4, "CreditCreated", "enum_activity_log_type_credit_created");
  public static creditCancelled = new EnumActivityLogType(5, "CreditCancelled", "enum_activity_log_type_credit_cancelled");
  public static creditCompleted = new EnumActivityLogType(6, "CreditCompleted", "enum_activity_log_type_credit_completed");
  public static taskCreated = new EnumActivityLogType(7, "TaskCreated", "enum_activity_log_type_task_created");
  public static taskCancelled = new EnumActivityLogType(8, "TaskCancelled", "enum_activity_log_type_task_cancelled");
  public static taskCompleted = new EnumActivityLogType(9, "TaskCompleted", "enum_activity_log_type_task_completed");
  public static communication = new EnumActivityLogType(10, "Communication", "enum_activity_log_type_communication");
  
  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumActivityLogType.allValues.push(this);
  }

  public get translation() { return translate(Key.fromString(this.translationKey)); }

  public isNone() { return this === EnumActivityLogType.none; }
  public isOrderCreated() { return this === EnumActivityLogType.orderCreated; }
  public isOrderCancelled() { return this === EnumActivityLogType.orderCancelled; }
  public isOrderCompleted() { return this === EnumActivityLogType.orderCompleted; }
  public isCreditCreated() { return this === EnumActivityLogType.creditCreated; }
  public isCreditCancelled() { return this === EnumActivityLogType.creditCancelled; }
  public isCreditCompleted() { return this === EnumActivityLogType.creditCompleted; }
  public isTaskCreated() { return this === EnumActivityLogType.taskCreated; }
  public isTaskCancelled() { return this === EnumActivityLogType.taskCancelled; }
  public isTaskCompleted() { return this === EnumActivityLogType.taskCompleted; }
  public isCommunication() { return this === EnumActivityLogType.communication; }

  public static getValues(): readonly EnumActivityLogType[] {
    return EnumActivityLogType.allValues;
  }

  public static getValuesExcept0(): readonly EnumActivityLogType[] {
    return EnumActivityLogType.allValues.filter((x) => x.key !== 0);
  }

  public static getByKey(key: number): EnumActivityLogType {
    const found = EnumActivityLogType.allValues.find(e => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumActivityLogType - Key not found: ${ key }`);
    }

    return found;
  }

  public static getByValue(name: string, def?: EnumActivityLogType): EnumActivityLogType {
    const found = name ? EnumActivityLogType.allValues.find(e => e.value.toLowerCase() === name.toLowerCase()) : null;
    if (!found) {
      if (def) {
        return def;
      }
      throw new Error(`EnumActivityLogType - Value not found: ${ name }`);
    }

    return found;
  }

  public equals(other: Maybe<EnumActivityLogType>): boolean {
    return this.key === other?.key;
  }
}